import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  SeatResource,
  useDetachResourceFromSeat,
  useGetRosterForIncident,
  useUpdateSeatResource,
} from 'hooks/incident/useRoster';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './SearchableSelect';

type SeatResourceRowProps = {
  seatResource: SeatResource;
};

const SeatResourceRow = ({ seatResource }: SeatResourceRowProps) => {
  const { data: rosterEntries = [] } = useGetRosterForIncident();

  const { mutate: updateSeatResource } = useUpdateSeatResource();
  const { mutate: detachResource, isPending: isDetaching } =
    useDetachResourceFromSeat();

  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [pointOfContact, setPointOfContact] =
    useState<SearchableSelectOption | null>(null);

  const pointOfContactOptions = useMemo(
    () =>
      rosterEntries.map((entry) => ({
        value: entry.userId,
        label: entry.userName ?? entry.emailAddress ?? entry.userId,
      })),
    [rosterEntries]
  );

  // The roster query may still be in flight when the row mounts, so the selection is seeded once it arrives. Reading it
  // out of the options list keeps the closed control and the menu on the same label.
  useEffect(() => {
    setPointOfContact(
      pointOfContactOptions.find(
        (option) => option.value === seatResource.pointOfContactUserId
      ) ?? null
    );
  }, [pointOfContactOptions, seatResource.pointOfContactUserId]);

  const onPointOfContactChange = (next: SearchableSelectOption | null) => {
    if (!next || next.value === seatResource.pointOfContactUserId) return;

    setPointOfContact(next);
    updateSeatResource({
      seatResourceId: seatResource.id,
      pointOfContactUserId: String(next.value),
    });
  };

  return (
    <>
      <div className="tw:flex tw:w-full tw:flex-col">
        <div className="tw:mb-2 tw:flex tw:flex-row tw:items-center tw:justify-between">
          <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
            <span>{seatResource.resourceName ?? 'Resource'}</span>
            {seatResource.resourceKind && (
              <Badge
                variant="secondary"
                className="tw:h-5 tw:px-1.5 tw:text-[10px]"
              >
                {seatResource.resourceKind}
              </Badge>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setShowRemoveConfirm(true)}
            aria-label={`Detach ${seatResource.resourceName ?? 'resource'}`}
          >
            <SharedIcon iconName="delete" />
          </Button>
        </div>
        <SearchableSelect
          id={`seat-resource-poc-${seatResource.id}`}
          label="Point of Contact"
          options={pointOfContactOptions}
          value={pointOfContact}
          onChange={onPointOfContactChange}
          placeholder="Select a point of contact"
        />
      </div>

      <ConfirmDeleteDialog
        open={showRemoveConfirm}
        title="Detach Resource Confirmation"
        description={`Are you sure you want to detach ${seatResource.resourceName ?? 'this resource'} from this seat? The resource itself stays on the incident.`}
        confirmLabel="Detach Resource"
        pending={isDetaching}
        onOpenChange={(open) => {
          if (!open) setShowRemoveConfirm(false);
        }}
        onConfirm={() => {
          detachResource(
            { seatResourceId: seatResource.id },
            {
              onSuccess: () => {
                toast.success('Resource detached from seat');
                setShowRemoveConfirm(false);
              },
            }
          );
        }}
      />
    </>
  );
};

export default SeatResourceRow;
